import { Image as ImageIconLucide, UploadCloud, X, Layers } from "lucide-react";
import SceneThumb from "./SceneThumb";

/**
 * MultiSceneDrop — scene input that switches between single-file and
 * batch (2-5 files) automatically based on what the user drops in.
 *
 * Single: looks identical to FileDrop.
 * Batch:  shows each scene as a thumbnail tile with a remove "×" badge,
 *         plus a "+ Add more" tile (up to 5 total).
 */
export default function MultiSceneDrop({ singleFile, files, batchMode, onPick, onRemoveAt, inputRef }) {
  if (!batchMode) {
    return (
      <label
        data-testid="catalog-scene-upload"
        className={`relative flex items-center justify-center w-full rounded-lg border-2 border-dashed transition cursor-pointer ${
          singleFile ? "border-[#D4AF37]/40 bg-[#141414]" : "border-white/10 hover:border-white/30 bg-[#0A0A0A]"
        }`}
        style={{ minHeight: 120 }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => onPick(e.target.files)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
        {singleFile ? (
          <div className="flex items-center gap-3 px-4 py-3">
            <ImageIconLucide className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-white/85 truncate max-w-[20ch]">{singleFile.name}</span>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); onPick(null); if (inputRef?.current) inputRef.current.value = ""; }}
              className="text-white/55 hover:text-white"
              aria-label="Clear file"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 px-4 py-3 text-center">
            <Layers className="w-5 h-5 text-white/55" />
            <span className="text-[11px] text-white/55">Drop file (or 2+ for batch)</span>
          </div>
        )}
      </label>
    );
  }

  // Batch mode — thumbnails grid + add-more tile.
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="catalog-scene-batch-grid">
      {files.map((f, idx) => (
        <div key={`${f.name}-${idx}`} className="relative aspect-square rounded-lg border border-[#D4AF37]/35 bg-[#141414] overflow-hidden group">
          <SceneThumb file={f} />
          <button
            type="button"
            onClick={() => onRemoveAt(idx)}
            data-testid={`catalog-scene-batch-remove-${idx}`}
            className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/80 border border-white/30 text-white/85 hover:bg-rose-500/50 hover:text-white"
            title="Remove"
          >
            <X className="w-3 h-3" />
          </button>
          <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] bg-black/55 px-1 rounded-sm">
            #{idx + 1}
          </span>
        </div>
      ))}
      {files.length < 5 && (
        <label
          data-testid="catalog-scene-batch-add"
          className="relative aspect-square rounded-lg border-2 border-dashed border-white/15 bg-[#0A0A0A] hover:border-[#D4AF37]/40 cursor-pointer flex flex-col items-center justify-center gap-1 text-white/55"
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              const incoming = Array.from(e.target.files || []);
              const merged = [...files, ...incoming];
              onPick(merged);
            }}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          <UploadCloud className="w-4 h-4" />
          <span className="text-[10px]">+ Add more</span>
        </label>
      )}
    </div>
  );
}
